import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import DataTable from './Common/DataTable';
import Modal from './Common/Modal';
import { useAuth } from '../contexts/AuthContext';
import { CURRENCY } from '../config';

export default function StudentManagement() {
  const { user } = useAuth();
  const isTeacher = user?.role === 'teacher';
  const isAdmin = user?.role === 'admin';
  const role = user?.role;

  const canSeeDiscount = ['admin', 'superintendent', 'principal', 'accountant'].includes(role);
  const canEditDiscount = ['admin', 'superintendent', 'principal'].includes(role);
  const canAddStudent = ['admin', 'superintendent', 'principal'].includes(role);

  const [students, setStudents] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [discountModal, setDiscountModal] = useState(null);
  const [discountForm, setDiscountForm] = useState({
    discount_percentage: 0,
    discount_reason: '',
  });

  // ─── 👇 NEW: Add Student modal state ───
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState('');
  const [parentForm, setParentForm] = useState({
    email: '', first_name: '', last_name: '', password: '',
    phone: '', address: '', cnic: '',
  });
  const [childForm, setChildForm] = useState({
    first_name: '', last_name: '', registration_number: '',
    date_of_birth: '', gender: 'Male', admission_date: '', class_id: '',
  });

  const [filterCampus, setFilterCampus] = useState('all');
  const [filterClass, setFilterClass] = useState('all');
  const [filterGender, setFilterGender] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [s, c, cls] = await Promise.all([
        api.get('/students/'),
        api.get('/campuses/').catch(() => ({ data: [] })),
        api.get('/classes/').catch(() => ({ data: [] })),
      ]);
      setStudents(s.data || []);
      setCampuses(c.data || []);
      setClasses(cls.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // ─── Filtering ───
  const filtered = students.filter(s => {
    if (filterCampus !== 'all') {
      const campus = campuses.find(c => c.id === parseInt(filterCampus));
      if (s.campus_name !== campus?.name) return false;
    }
    if (filterClass !== 'all' && s.class_name !== filterClass) return false;
    if (filterGender !== 'all' && s.gender !== filterGender) return false;
    if (search) {
      const q = search.toLowerCase();
      const blob = `${s.student_name || ''} ${s.registration_number || ''} ${s.roll_no || ''} ${s.guardian_name || ''}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  const total = filtered.length;
  const males = filtered.filter(s => s.gender === 'Male').length;
  const females = filtered.filter(s => s.gender === 'Female').length;
  const uniqueClasses = new Set(filtered.map(s => s.class_name)).size;
  const discountedCount = filtered.filter(s => (s.discount_percentage || 0) > 0).length;

  // ─── Discount edit ───
  const openDiscountModal = (student) => {
    setDiscountModal(student);
    setDiscountForm({
      discount_percentage: student.discount_percentage || 0,
      discount_reason: student.discount_reason || '',
    });
  };

  const handleSaveDiscount = async () => {
    try {
      const pct = parseFloat(discountForm.discount_percentage) || 0;
      if (pct < 0 || pct > 100) {
        alert('Discount must be between 0 and 100');
        return;
      }
      await api.put(`/students/${discountModal.id}/discount`, {
        discount_percentage: pct,
        discount_reason: discountForm.discount_reason || null,
      });
      setDiscountModal(null);
      fetchData();
      alert('✅ Discount updated');
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  // ═══════════════════════════════════════════════════════════
  // 👇 NEW: Add Student handler (mirrors AccountCreation flow)
  // ═══════════════════════════════════════════════════════════
  const resetAddForm = () => {
    setParentForm({ email: '', first_name: '', last_name: '', password: '',
      phone: '', address: '', cnic: '' });
    setChildForm({ first_name: '', last_name: '', registration_number: '',
      date_of_birth: '', gender: 'Male', admission_date: '', class_id: '' });
    setAddError('');
  };

  const handleCreateStudent = async () => {
    setAddError('');

    // Validation
    if (!parentForm.first_name || !parentForm.last_name || !parentForm.email) {
      setAddError('Parent first name, last name, and email are required.');
      return;
    }
    if (parentForm.password.length < 8) {
      setAddError('Parent password must be at least 8 characters.');
      return;
    }
    if (!childForm.first_name || !childForm.last_name) {
      setAddError('Student first name and last name are required.');
      return;
    }
    if (!childForm.class_id) {
      setAddError('Please select a class for the student.');
      return;
    }

    setAddBusy(true);
    try {
      // ─── 1. Find or create parent ───
      const allUsers = await api.get('/users/');
      let existingParent = null;

      if (parentForm.cnic?.trim()) {
        existingParent = allUsers.data.find(u =>
          u.role === 'parent' && u.cnic && u.cnic === parentForm.cnic.trim()
        );
      }
      if (!existingParent && parentForm.email) {
        existingParent = allUsers.data.find(u =>
          u.role === 'parent' && u.email.toLowerCase() === parentForm.email.toLowerCase()
        );
      }

      let parentId;
      if (existingParent) {
        parentId = existingParent.id;
      } else {
        const parentRes = await api.post('/users/', {
          email: parentForm.email,
          first_name: parentForm.first_name,
          last_name: parentForm.last_name,
          password: parentForm.password,
          phone: parentForm.phone || '',
          address: parentForm.address || '',
          cnic: parentForm.cnic || null,
          role: 'parent',
        });
        parentId = parentRes.data.id;
      }

      // ─── 2. Create Student User (login disabled) ───
      const studentEmail = `student_${Date.now()}_${Math.random().toString(36).substring(2, 7)}@school.local`;
      const studentUserRes = await api.post('/users/', {
        email: studentEmail,
        first_name: childForm.first_name,
        last_name: childForm.last_name,
        password: 'dummy' + Date.now(),
        phone: '',
        address: parentForm.address || '',
        cnic: null,
        role: 'student',
        is_active: True,
      });
      const studentUserId = studentUserRes.data.id;

      // ─── 3. Create Student record ───
      const studentRes = await api.post('/students/', {
        user_id: studentUserId,
        class_id: parseInt(childForm.class_id),
        registration_number: childForm.registration_number || '',
        roll_no: null,
        admission_date: childForm.admission_date || null,
        date_of_birth: childForm.date_of_birth || null,
        gender: childForm.gender,
        address: parentForm.address || '',
        guardian_name: `${parentForm.first_name} ${parentForm.last_name}`,
        guardian_phone: parentForm.phone || '',
        guardian_email: parentForm.email,
      });
      const studentId = studentRes.data.id;

      // ─── 4. Link parent → student ───
      try {
        await api.post('/parent-students/', {
          parent_id: parentId,
          student_id: studentId,
          relationship: 'Parent',
          is_primary: true,
        });
      } catch (linkErr) {
        if (linkErr.response?.status !== 400) throw linkErr;
      }

      alert('✅ Student created and linked successfully!');
      setAddModalOpen(false);
      resetAddForm();
      fetchData();
    } catch (err) {
      setAddError(err.response?.data?.detail || err.message);
    } finally {
      setAddBusy(false);
    }
  };

  // ─── Columns ───
  const columns = [
    'Roll #', 'Registration #', 'Name', 'Class', 'Gender', 'DOB',
    'Guardian', 'Guardian Phone',
    ...(!isTeacher ? ['Campus'] : []),
    ...(canSeeDiscount ? ['Discount'] : []),
    'Status', 'Actions'
  ];

  const data = filtered.map(s => {
    const row = {
      'Roll #': (
        <span className="px-2 py-1 rounded text-xs font-bold bg-blue-100 text-blue-800">
          {s.roll_no || '—'}
        </span>
      ),
      'Registration #': (
        <span className="text-xs text-gray-600 font-mono">{s.registration_number || '—'}</span>
      ),
      Name: s.student_name || 'Unknown',
      Class: s.class_name || '—',
      Gender: (
        <span className={`px-2 py-1 rounded text-xs ${
          s.gender === 'Male' ? 'bg-blue-100 text-blue-800'
          : s.gender === 'Female' ? 'bg-pink-100 text-pink-800'
          : 'bg-gray-100 text-gray-800'
        }`}>{s.gender || '—'}</span>
      ),
      DOB: s.date_of_birth || '—',
      Guardian: s.guardian_name || '—',
      'Guardian Phone': s.guardian_phone || '—',
      Status: (
        <span className={`px-2 py-1 rounded text-xs ${s.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {s.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
      Actions: (
        <div className="flex gap-1">
          <button onClick={() => setSelectedStudent(s)}
            className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
            title="View details">
            <i className="fas fa-eye"></i>
          </button>
          {canEditDiscount && (
            <button onClick={() => openDiscountModal(s)}
              className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded hover:bg-purple-200"
              title="Edit discount">
              <i className="fas fa-percent"></i>
            </button>
          )}
        </div>
      ),
    };

    if (!isTeacher) row.Campus = s.campus_name || '—';

    if (canSeeDiscount) {
      const pct = s.discount_percentage || 0;
      if (pct > 0) {
        row.Discount = (
          <span className={`px-2 py-1 rounded text-xs font-bold bg-green-100 text-green-800 ${
            canEditDiscount ? 'cursor-pointer hover:bg-green-200' : ''
          }`}
            title={s.discount_reason || 'No reason provided'}
            onClick={() => canEditDiscount && openDiscountModal(s)}>
            <i className="fas fa-percent mr-1"></i>{pct}% off
          </span>
        );
      } else {
        row.Discount = canEditDiscount ? (
          <button onClick={() => openDiscountModal(s)}
            className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200">
            <i className="fas fa-plus mr-1"></i> Add
          </button>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        );
      }
    }

    return row;
  });

  const statsCols = canSeeDiscount ? 'grid-cols-5' : 'grid-cols-4';

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold">
          {isTeacher ? '🎓 My Class Students' : '🎓 Students'}
        </h2>
        <div className="flex gap-2">
          <button onClick={fetchData}
            className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
            <i className="fas fa-sync-alt mr-1"></i> Refresh
          </button>
          {/* 👇 NEW: Add Student button */}
          {canAddStudent && (
            <button
              onClick={() => { resetAddForm(); setAddModalOpen(true); }}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm">
              <i className="fas fa-plus mr-1"></i> Add Student
            </button>
          )}
        </div>
      </div>

      <div className={`grid ${statsCols} gap-3 mb-4`}>
        <div className="bg-white p-4 rounded shadow border-l-4 border-blue-500">
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <i className="fas fa-user-graduate text-blue-500"></i> Total Students
          </p>
          <p className="text-2xl font-bold text-blue-600">{total}</p>
        </div>
        <div className="bg-white p-4 rounded shadow border-l-4 border-indigo-500">
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <i className="fas fa-male text-indigo-500"></i> Male
          </p>
          <p className="text-2xl font-bold text-indigo-600">{males}</p>
        </div>
        <div className="bg-white p-4 rounded shadow border-l-4 border-pink-500">
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <i className="fas fa-female text-pink-500"></i> Female
          </p>
          <p className="text-2xl font-bold text-pink-600">{females}</p>
        </div>
        <div className="bg-white p-4 rounded shadow border-l-4 border-purple-500">
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <i className="fas fa-layer-group text-purple-500"></i> Classes
          </p>
          <p className="text-2xl font-bold text-purple-600">{uniqueClasses}</p>
        </div>
        {canSeeDiscount && (
          <div className="bg-white p-4 rounded shadow border-l-4 border-green-500">
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <i className="fas fa-percent text-green-500"></i> Discounted
            </p>
            <p className="text-2xl font-bold text-green-600">{discountedCount}</p>
          </div>
        )}
      </div>

      <div className="bg-white p-3 rounded shadow mb-4 flex flex-wrap gap-3 items-center">
        {!isTeacher && (
          <select className="p-2 border rounded text-sm" value={filterCampus}
            onChange={(e) => setFilterCampus(e.target.value)}>
            <option value="all">All Campuses</option>
            {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}

        <select className="p-2 border rounded text-sm" value={filterClass}
          onChange={(e) => setFilterClass(e.target.value)}>
          <option value="all">All Classes</option>
          {[...new Set(students.map(s => s.class_name))].filter(Boolean).sort().map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <select className="p-2 border rounded text-sm" value={filterGender}
          onChange={(e) => setFilterGender(e.target.value)}>
          <option value="all">All Genders</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Other">Other</option>
        </select>

        <input type="text" placeholder="Search name, reg no, roll no, guardian..."
          className="flex-1 min-w-[200px] p-2 border rounded text-sm"
          value={search} onChange={(e) => setSearch(e.target.value)} />

        <span className="text-sm text-gray-500">
          Showing {filtered.length} of {students.length}
        </span>
      </div>

      <div className="bg-white p-4 rounded shadow">
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : data.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <i className="fas fa-inbox text-4xl mb-3"></i>
            <p>No students found.</p>
          </div>
        ) : (
          <DataTable columns={columns} data={data} />
        )}
      </div>

      {/* ══════════════════════════════════════════════════════
          👇 NEW: Add Student Modal
      ══════════════════════════════════════════════════════ */}
      <Modal
        isOpen={addModalOpen}
        onClose={() => { setAddModalOpen(false); resetAddForm(); }}
        title="🎓 Add New Student"
        onSave={handleCreateStudent}
        saveLabel={addBusy ? 'Creating…' : 'Create Student'}
        size="lg"
      >
        <div className="space-y-4">
          {addError && (
            <div className="bg-red-50 text-red-600 p-2 rounded text-sm border border-red-200">
              ⚠️ {addError}
            </div>
          )}

          {/* Parent / Guardian section */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
              <i className="fas fa-user text-blue-500"></i> Parent / Guardian
            </h4>
            <p className="text-xs text-gray-500 mb-3">
              If this parent already exists (same CNIC or email), the new student will be linked to them automatically.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <input className="w-full p-2 border rounded" placeholder="First Name *"
                value={parentForm.first_name}
                onChange={e => setParentForm({ ...parentForm, first_name: e.target.value })} />
              <input className="w-full p-2 border rounded" placeholder="Last Name *"
                value={parentForm.last_name}
                onChange={e => setParentForm({ ...parentForm, last_name: e.target.value })} />
              <input className="w-full p-2 border rounded col-span-2" type="email"
                placeholder="Email (used for login) *"
                value={parentForm.email}
                onChange={e => setParentForm({ ...parentForm, email: e.target.value })} />
              <input className="w-full p-2 border rounded" type="password"
                placeholder="Password (min 8 chars) *"
                value={parentForm.password}
                onChange={e => setParentForm({ ...parentForm, password: e.target.value })} />
              <input className="w-full p-2 border rounded" placeholder="Phone"
                value={parentForm.phone}
                onChange={e => setParentForm({ ...parentForm, phone: e.target.value })} />
              <input className="w-full p-2 border rounded col-span-2" placeholder="Address"
                value={parentForm.address}
                onChange={e => setParentForm({ ...parentForm, address: e.target.value })} />
              <div className="col-span-2">
                <input className="w-full p-2 border rounded"
                  placeholder="CNIC / ID Number (used to link siblings)"
                  value={parentForm.cnic}
                  onChange={e => setParentForm({ ...parentForm, cnic: e.target.value })} />
                <p className="text-xs text-gray-400 mt-1">
                  💡 Same CNIC = siblings linked to the same parent.
                </p>
              </div>
            </div>
          </div>

          {/* Student section */}
          <div className="border rounded-lg p-4 bg-white">
            <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
              <i className="fas fa-child text-green-500"></i> Student Details
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <input className="w-full p-2 border rounded" placeholder="Student First Name *"
                value={childForm.first_name}
                onChange={e => setChildForm({ ...childForm, first_name: e.target.value })} />
              <input className="w-full p-2 border rounded" placeholder="Student Last Name *"
                value={childForm.last_name}
                onChange={e => setChildForm({ ...childForm, last_name: e.target.value })} />
              <input className="w-full p-2 border rounded" placeholder="Registration Number"
                value={childForm.registration_number}
                onChange={e => setChildForm({ ...childForm, registration_number: e.target.value })} />

              <div>
                <label className="block text-xs text-gray-500 mb-1">Class *</label>
                <select className="w-full p-2 border rounded"
                  value={childForm.class_id}
                  onChange={e => setChildForm({ ...childForm, class_id: e.target.value })}>
                  <option value="">— Select a class —</option>
                  {classes.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.section ? ` · ${c.section}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Gender</label>
                <select className="w-full p-2 border rounded"
                  value={childForm.gender}
                  onChange={e => setChildForm({ ...childForm, gender: e.target.value })}>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Date of Birth</label>
                <input className="w-full p-2 border rounded" type="date"
                  value={childForm.date_of_birth}
                  onChange={e => setChildForm({ ...childForm, date_of_birth: e.target.value })} />
              </div>

              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Admission Date</label>
                <input className="w-full p-2 border rounded" type="date"
                  value={childForm.admission_date}
                  onChange={e => setChildForm({ ...childForm, admission_date: e.target.value })} />
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Student Details Modal (unchanged) */}
      <Modal
        isOpen={!!selectedStudent}
        onClose={() => setSelectedStudent(null)}
        title="Student Details"
        size="lg"
        showFooter={false}
      >
        {selectedStudent && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 pb-4 border-b">
              <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-2xl font-bold">
                {(selectedStudent.student_name || '?')[0]}
              </div>
              <div>
                <h3 className="text-xl font-bold">{selectedStudent.student_name}</h3>
                <p className="text-sm text-gray-500">
                  Roll No: <strong>{selectedStudent.roll_no || '—'}</strong>
                  {' · '}
                  Registration: <strong className="font-mono">{selectedStudent.registration_number || '—'}</strong>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-xs text-gray-500">Class</p><p className="font-medium">{selectedStudent.class_name || '—'}</p></div>
              <div><p className="text-xs text-gray-500">Campus</p><p className="font-medium">{selectedStudent.campus_name || '—'}</p></div>
              <div><p className="text-xs text-gray-500">Gender</p><p className="font-medium">{selectedStudent.gender || '—'}</p></div>
              <div><p className="text-xs text-gray-500">DOB</p><p className="font-medium">{selectedStudent.date_of_birth || '—'}</p></div>
              <div><p className="text-xs text-gray-500">Admission Date</p><p className="font-medium">{selectedStudent.admission_date || '—'}</p></div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <i className="fas fa-user-shield text-blue-500"></i> Guardian Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-gray-500">Guardian Name</p><p className="font-medium">{selectedStudent.guardian_name || '—'}</p></div>
                <div><p className="text-xs text-gray-500">Phone</p><p className="font-medium">{selectedStudent.guardian_phone || '—'}</p></div>
                <div className="col-span-2"><p className="text-xs text-gray-500">Email</p><p className="font-medium">{selectedStudent.guardian_email || '—'}</p></div>
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-xs text-gray-500">Address</p>
              <p className="font-medium">{selectedStudent.address || '—'}</p>
            </div>
          </div>
        )}
      </Modal>

      {/* Discount Modal (unchanged) */}
      {canEditDiscount && (
        <Modal
          isOpen={!!discountModal}
          onClose={() => setDiscountModal(null)}
          title={`Fee Discount — ${discountModal?.student_name || ''}`}
          onSave={handleSaveDiscount}
          saveLabel="Save Discount"
        >
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded text-sm">
              <p><strong>Student:</strong> {discountModal?.student_name}</p>
              <p><strong>Class:</strong> {discountModal?.class_name}</p>
              <p><strong>Reg #:</strong> <span className="font-mono">{discountModal?.registration_number}</span></p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Discount Percentage</label>
              <div className="flex gap-2 flex-wrap mb-2">
                {[0, 25, 50, 75, 100].map(p => (
                  <button key={p} type="button"
                    onClick={() => setDiscountForm({ ...discountForm, discount_percentage: p })}
                    className={`px-3 py-1 text-xs rounded border font-semibold ${
                      Number(discountForm.discount_percentage) === p
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                    }`}>{p}%</button>
                ))}
              </div>
              <input type="number" className="w-full p-2 border rounded"
                value={discountForm.discount_percentage}
                onChange={e => setDiscountForm({ ...discountForm, discount_percentage: e.target.value })}
                min="0" max="100" placeholder="Custom percentage" />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Reason</label>
              <select className="w-full p-2 border rounded mb-2"
                value={discountForm.discount_reason}
                onChange={e => setDiscountForm({ ...discountForm, discount_reason: e.target.value })}>
                <option value="">— Select a reason —</option>
                <option value="Staff child discount">Staff child discount</option>
                <option value="Sibling discount">Sibling discount</option>
                <option value="Scholarship">Scholarship</option>
                <option value="Financial hardship">Financial hardship</option>
                <option value="Orphan support">Orphan support</option>
                <option value="Merit-based">Merit-based</option>
                <option value="Other">Other</option>
              </select>
              <input className="w-full p-2 border rounded"
                placeholder="Or type a custom reason..."
                value={discountForm.discount_reason}
                onChange={e => setDiscountForm({ ...discountForm, discount_reason: e.target.value })} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}